import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';

interface SepticTankCleaningDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

export default function SepticTankCleaningDrawer({ isOpen, onClose, currentLanguage }: SepticTankCleaningDrawerProps) {
  const [formData, setFormData] = useState({
    wrd_id: '',
    svm_id: '',
    address: '',
    trips: 1,
    amount: 2500,
    description: ''
  });
  const [wards, setWards] = useState<Array<{ wrd_id: number; wrd_nm: string; wrd_nu?: number }>>([]);
  const [sachivalayams, setSachivalayams] = useState<Array<{ svm_id: number; svm_nm: string }>>([]);
  const [locationWard, setLocationWard] = useState<{ wrd_id: number; wrd_nu?: number; wrd_nm: string } | null>(null);
  const [locationSachivalayam, setLocationSachivalayam] = useState<{ svm_id: number; svm_nm: string; svm_apfs_cd?: string } | null>(null);
  const [showWardDropdown, setShowWardDropdown] = useState(true);
  const [showSachivalayamDropdown, setShowSachivalayamDropdown] = useState(true);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [submittedTicketId, setSubmittedTicketId] = useState<number | null>(null);

  const TRIP_AMOUNT = 2500; // Rs.2000 per trip

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      fetchWards();
      // Reset form
      setFormData({
        wrd_id: '',
        svm_id: '',
        address: '',
        trips: 1,
        amount: TRIP_AMOUNT,
        description: ''
      });
      setLocationWard(null);
      setLocationSachivalayam(null);
      setShowWardDropdown(true);
      setShowSachivalayamDropdown(true);
      setSachivalayams([]);
      setSubmitError(null);
      setLatitude(null);
      setLongitude(null);
      setShowPaymentScreen(false);
      setPaymentDetails(null);
      setSubmittedTicketId(null);
    }
  }, [isOpen]);

  // Calculate amount when trips change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      amount: prev.trips * TRIP_AMOUNT
    }));
  }, [formData.trips]);

  const fetchWards = async () => {
    try {
      const response = await service.get<Array<{ wrd_id: number; wrd_nm: string; wrd_nu?: number }>>('/open/wards');
      if (response.status === 200 && Array.isArray(response.data)) {
        setWards(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching wards:', err);
    }
  };

  const fetchSachivalayams = async (wardId: number) => {
    try {
      const response: any = await service.post(`/open/ward/sachivalayams/${wardId}`, {});
      const payload =
        Array.isArray(response?.data) ? response.data :
        Array.isArray(response?.data?.data) ? response.data.data :
        [];
      if (response?.status === 200 && Array.isArray(payload)) {
        setSachivalayams(payload);
      } else {
        setSachivalayams([]);
      }
    } catch (err: any) {
      console.error('Error fetching sachivalayams:', err);
      setSachivalayams([]);
    }
  };

  const handleShareLocation = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          
          try {
            // Call chatbot API to get ward and sachivalayam based on location
            const response: any = await service.get(`/open/chatbot/${lat}/${lng}/sachivalayam`);
            
            if (response && response.data) {
              const locationData = response.data;
              
              // Set ward if available
              if (locationData.wrd && locationData.wrd.length > 0) {
                const ward = locationData.wrd[0];
                setLocationWard({
                  wrd_id: ward.wrd_id,
                  wrd_nu: ward.wrd_nu,
                  wrd_nm: ward.wrd_nm
                });
                setFormData(prev => ({ ...prev, wrd_id: String(ward.wrd_id) }));
                setShowWardDropdown(false);
                
                // Fetch sachivalayams for this ward
                await fetchSachivalayams(ward.wrd_id);
              }
              
              // Set sachivalayam if available
              if (locationData.svm && locationData.svm.length > 0) {
                const svm = locationData.svm[0];
                setLocationSachivalayam({
                  svm_id: svm.svm_id,
                  svm_nm: svm.svm_nm,
                  svm_apfs_cd: svm.svm_apfs_cd
                });
                setFormData(prev => ({ ...prev, svm_id: String(svm.svm_id) }));
                setShowSachivalayamDropdown(false);
              }
              
              alert(currentLanguage === 'en' 
                ? 'Location shared successfully' 
                : 'స్థానం విజయవంతంగా భాగస్వామ్యం చేయబడింది');
            }
          } catch (err: any) {
            console.error('Error fetching location data:', err);
            alert(currentLanguage === 'en' 
              ? 'Location shared but could not fetch ward/sachivalayam details' 
              : 'స్థానం భాగస్వామ్యం చేయబడింది కానీ వార్డ్/సచివాలయం వివరాలను పొందలేకపోయాము');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(currentLanguage === 'en' 
            ? 'Unable to get your location. Please try again.' 
            : 'మీ స్థానాన్ని పొందలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.');
        }
      );
    } else {
      alert(currentLanguage === 'en' 
        ? 'Geolocation is not supported by your browser.' 
        : 'మీ బ్రౌజర్ జియోలొకేషన్‌ను మద్దతు ఇవ్వదు.');
    }
  };

  const handleWardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const wardIdStr = e.target.value;
    const wardId = parseInt(wardIdStr, 10);
    setFormData({ ...formData, wrd_id: wardIdStr, svm_id: '' });
    setSachivalayams([]);
    setLocationWard(null);
    setShowWardDropdown(true);
    setLocationSachivalayam(null);
    setShowSachivalayamDropdown(true);
    
    if (!isNaN(wardId)) {
      fetchSachivalayams(wardId);
    }
  };

  const handleTripsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trips = parseInt(e.target.value, 10) || 1;
    setFormData({
      ...formData,
      trips: trips > 0 ? trips : 1,
      amount: (trips > 0 ? trips : 1) * TRIP_AMOUNT
    });
  };

  const handleVerifyPayment = async () => {
    if (!paymentDetails || !submittedTicketId) return;
    
    setIsVerifyingPayment(true);
    try {
      const reqBody = {
        bnk_upi_trns_ref_nu: paymentDetails.mrcht_trnsn_ref_nu,
        cpay_trnsn_ref_nu: paymentDetails.cpay_trnsn_ref_nu,
        tcketId: submittedTicketId
      };

      const response: any = await service.post('/public/web/verify/payment', reqBody);

      if (response && response.status === 200 && response.data) {
        const finalResults = response.data?.data?.finalResults;
        if (finalResults) {
          setPaymentDetails({
            ...paymentDetails,
            trns_sts: finalResults.trns_sts,
            invce_no: finalResults.invce_id,
            rcpt_url: finalResults.rcpt_url
          });
          
          if (finalResults.trns_sts === 'success') {
            alert(currentLanguage === 'en' 
              ? 'Payment successful!' 
              : 'చెల్లింపు విజయవంతంగా!');
            // Close payment screen and drawer
            setShowPaymentScreen(false);
            onClose();
          } else {
            alert(currentLanguage === 'en' 
              ? 'Payment verification completed. Status: ' + finalResults.trns_sts 
              : 'చెల్లింపు ధృవీకరణ పూర్తయింది. స్థితి: ' + finalResults.trns_sts);
          }
        }
      } else {
        throw new Error(response?.message || 'Failed to verify payment');
      }
    } catch (err: any) {
      console.error('Error verifying payment:', err);
      alert(err.message || (currentLanguage === 'en' 
        ? 'Failed to verify payment. Please try again.' 
        : 'చెల్లింపును ధృవీకరించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation - check both location-based and form values
    const hasWard = locationWard?.wrd_id || formData.wrd_id;
    const hasSvm = locationSachivalayam?.svm_id || formData.svm_id;
    
    if (!hasWard) {
      setSubmitError(currentLanguage === 'en' ? 'Please select Ward' : 'దయచేసి వార్డ్ ఎంచుకోండి');
      return;
    }
    if (!hasSvm) {
      setSubmitError(currentLanguage === 'en' ? 'Please select Sachivalayam' : 'దయచేసి సచివాలయం ఎంచుకోండి');
      return;
    }
    if (!formData.address.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please enter address' : 'దయచేసి చిరునామా నమోదు చేయండి');
      return;
    }
    if (!formData.description.trim()) {
      setSubmitError(currentLanguage === 'en' ? 'Please describe your request' : 'దయచేసి మీ అభ్యర్థనను వివరించండి');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Use location-based values if available, otherwise use selected dropdown values
      const finalWrdId = locationWard?.wrd_id || (formData.wrd_id ? parseInt(formData.wrd_id, 10) : 0);
      const finalSvmId = locationSachivalayam?.svm_id || (formData.svm_id ? parseInt(formData.svm_id, 10) : 0);

      if (!finalWrdId || finalWrdId === 0) {
        setSubmitError(currentLanguage === 'en' ? 'Please select Ward' : 'దయచేసి వార్డ్ ఎంచుకోండి');
        setIsSubmitting(false);
        return;
      }
      if (!finalSvmId || finalSvmId === 0) {
        setSubmitError(currentLanguage === 'en' ? 'Please select Sachivalayam' : 'దయచేసి సచివాలయం ఎంచుకోండి');
        setIsSubmitting(false);
        return;
      }
    
      // Citizen details from localStorage
      const ctznNm = localStorage.getItem('citizenName') || '';
      const ctznPh = localStorage.getItem('citizenMobile') || '';

      const postData = {
        data: {
          wrd_id: finalWrdId,
          svm_id: finalSvmId,
          prty_id: 1,
          Address: formData.address,
          amnt: formData.amount,
          lng_dscn_tx: formData.description,
          tckt_type_id: 3, 
          tckt_ctgry_id: 30,
          tkt_srce_id: 11,
          tckt_stts_id: 17,
          ctzn_nm: ctznNm,
          ctzn_ph: ctznPh,
          eRqustCnt: "SEPTIC TANK CLEANING",
          trpsCnt: formData.trips,
          clctn_type_id: 9,
          vrynce_type: "30",
          trpsAmnt: TRIP_AMOUNT,
          prdctGrpDtls: {
            prdct_grp_id: 91,
            prdct_grp_nm: "Paid Towards Desludging"
          }
        }
      };

      const response: any = await service.post('/public/web/e-request/tickets', postData);

      if (response && response.status === 200) {
        // Check if payment details are in the response
        let paymentData = null;
        let ticketId = null;
        let assignedTo = null;
        
        if (response.data && Array.isArray(response.data)) {
          // Get ticket ID from response.data[2]
          if (response.data.length > 2) {
            ticketId = response.data[2];
          }
          
          // Get assigned user from response.data[1]
          if (response.data.length > 1 && Array.isArray(response.data[1]) && response.data[1].length > 0) {
            assignedTo = response.data[1][0].fst_nm;
          }
          
          // Get payment data from response.data[3]
          if (response.data.length > 3 && response.data[3]?.data?.cpay_status === 'success') {
            paymentData = response.data[3].data.data;
            if (assignedTo) {
              paymentData.asgn_usr_nm = assignedTo;
            }
          }
        }
        
        if (paymentData && paymentData.qr_code) {
          // Show payment screen with QR code
          setPaymentDetails(paymentData);
          setSubmittedTicketId(ticketId);
          setShowPaymentScreen(true);
        } else {
          // No payment required, just show success
          alert(currentLanguage === 'en' 
            ? `Septic Tank Cleaning request submitted successfully!${ticketId ? ` Ticket ID: ${ticketId}` : ''}` 
            : `సెప్టిక్ ట్యాంక్ శుభ్రత అభ్యర్థన విజయవంతంగా సమర్పించబడింది!${ticketId ? ` టికెట్ ID: ${ticketId}` : ''}`);
          // Close drawer
          onClose();
        }
      } else {
        throw new Error(response?.message || 'Failed to submit request');
      }
    } catch (err: any) {
      console.error('Error submitting septic tank cleaning request:', err);
      setSubmitError(err.message || (currentLanguage === 'en' 
        ? 'Failed to submit request. Please try again.' 
        : 'అభ్యర్థనను సమర్పించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && !showPaymentScreen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 z-[10000]"
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[10000] flex flex-col overflow-y-auto"
            >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-4 flex items-center justify-between shadow sticky top-0 z-10">
              <p className="text-sm font-bold uppercase tracking-wide flex-1">
                SEPTIC TANK CLEANING
              </p>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-100 transition p-1"
                aria-label="Close form"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 p-5 space-y-4">
              <p className="text-xs text-red-600 mb-2">
                {currentLanguage === 'en' 
                  ? 'Fields marked with * are mandatory.' 
                  : '* గుర్తు పెట్టిన ఫీల్డ్‌లు తప్పనిసరి.'}
              </p>

              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Share Location */}
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    {currentLanguage === 'en' 
                      ? 'Click Button To share Current Location' 
                      : 'ప్రస్తుత స్థానాన్ని భాగస్వామ్యం చేయడానికి బటన్ క్లిక్ చేయండి'}
                  </p>
                  <button
                    type="button"
                    onClick={handleShareLocation}
                    className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg border border-orange-300 transition-colors"
                  >
                    {currentLanguage === 'en' ? 'Share Location' : 'స్థానాన్ని భాగస్వామ్యం చేయండి'}
                  </button>
                </div>

                {/* Select Ward */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Select Ward' : 'వార్డ్ ఎంచుకోండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  {!showWardDropdown && locationWard ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                      <p className="text-sm text-orange-600 font-medium">
                        {locationWard.wrd_nu ? `W${locationWard.wrd_nu}` : locationWard.wrd_nm}
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.wrd_id}
                      onChange={handleWardChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      required
                    >
                      <option value="">{currentLanguage === 'en' ? 'Ward' : 'వార్డ్'}</option>
                      {wards.map((ward) => (
                        <option key={ward.wrd_id} value={String(ward.wrd_id)}>
                          {ward.wrd_nm}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Select Sachivalayam */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Select Sachivalayam' : 'సచివాలయం ఎంచుకోండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  {!showSachivalayamDropdown && locationSachivalayam ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                      <p className="text-sm text-orange-600 font-medium">
                        {locationSachivalayam.svm_nm}
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.svm_id}
                      onChange={(e) => {
                        setFormData({ ...formData, svm_id: e.target.value });
                        setLocationSachivalayam(null);
                        setShowSachivalayamDropdown(true);
                      }}
                      disabled={!formData.wrd_id || sachivalayams.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">{currentLanguage === 'en' ? 'Select Sachivalayam' : 'సచివాలయం ఎంచుకోండి'}</option>
                      {sachivalayams.map((svm) => (
                        <option key={svm.svm_id} value={String(svm.svm_id)}>
                          {svm.svm_nm}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Enter Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Enter Address' : 'చిరునామా నమోదు చేయండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={currentLanguage === 'en' ? 'Complete Address' : 'పూర్తి చిరునామా'}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                    required
                  />
                </div>

                {/* Service Charge Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    {currentLanguage === 'en' 
                      ? 'SEPTIC TANK CLEANING charged amount Rs.2500/- per 1 trip.'
                      : 'సెప్టిక్ ట్యాంక్ శుభ్రత 1 ట్రిప్‌కు రూ.2500/- ఛార్జ్ చేయబడుతుంది.'}
                  </p>
                </div>

                {/* No. of Trips and Amount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'No.of Trips' : 'ట్రిప్‌ల సంఖ్య'}
                      <span className="text-red-600 ml-1">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.trips}
                      onChange={handleTripsChange}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {currentLanguage === 'en' ? 'Amount' : 'మొత్తం'}
                      <span className="text-red-600 ml-1">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.amount}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                    />
                  </div>
                </div>

                {/* Describe your Request */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'Describe your Request' : 'మీ అభ్యర్థనను వివరించండి'}
                    <span className="text-red-600 ml-1">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={currentLanguage === 'en' ? 'Describe your Request' : 'మీ అభ్యర్థనను వివరించండి'}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm resize-none"
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {currentLanguage === 'en' ? 'Submitting...' : 'సమర్పిస్తోంది...'}
                      </span>
                    ) : (
                      'SUBMIT'
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
        )}
      </AnimatePresence>

      {/* Payment Screen Drawer */}
      <AnimatePresence>
        {showPaymentScreen && paymentDetails && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPaymentScreen(false)}
              className="fixed inset-0 bg-black/50 z-[10000]"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-4 flex items-center justify-between shadow sticky top-0 z-10">
                <p className="text-sm font-bold uppercase tracking-wide flex-1">
                  SEPTIC TANK CLEANING
                </p>
                <button
                  onClick={() => setShowPaymentScreen(false)}
                  className="text-white hover:text-gray-100 transition p-1"
                  aria-label="Close payment screen"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {/* Back Button */}
              <div className="px-5 py-3 border-b border-gray-200">
                <button
                  onClick={() => setShowPaymentScreen(false)}
                  className="text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-2"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                  <span className="text-sm font-medium">Back</span>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 p-5 space-y-4">
                {/* Ticket Information */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-red-600">Ticket Id:</span>
                    <span className="text-sm text-gray-900">{submittedTicketId || paymentDetails.tckt_id || 'N/A'}</span>
                  </div>
                  {paymentDetails.asgn_usr_nm && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-red-600">Assigned To:</span>
                      <span className="text-sm text-gray-900">{paymentDetails.asgn_usr_nm}</span>
                    </div>
                  )}
                </div>

                {/* Payment Instructions */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    {currentLanguage === 'en' 
                      ? 'Please scan qr code to complete payment using any UPI enabled payment apps (GPay, PhonePay, Paytm, Amazonpay, etc,..)' 
                      : 'దయచేసి ఏదైనా UPI ప్రారంభించబడిన చెల్లింపు యాప్‌లను (GPay, PhonePay, Paytm, Amazonpay, మొదలైనవి) ఉపయోగించి చెల్లింపును పూర్తి చేయడానికి qr కోడ్‌ను స్కాన్ చేయండి'}
                  </p>
                </div>

                {/* QR Code */}
                {paymentDetails.qr_code && (
                  <div className="bg-white border border-gray-300 rounded-2xl p-4 flex items-center justify-center">
                    <div className="w-full max-w-xs">
                      <img 
                        src={paymentDetails.qr_code} 
                        alt="QR Code" 
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}

                {/* Transaction Details Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Transaction No</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.mrcht_trnsn_ref_nu || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">cPay No</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.cpay_trnsn_ref_nu || 'N/A'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Receipt No</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.invce_no || 'NA'}</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Amount</td>
                        <td className="px-3 py-2 text-sm text-gray-900">Rs.{paymentDetails.amount || '0.00'}/-</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50">Date&Time</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{paymentDetails.trnsn_intlz_ts || 'N/A'}</td>
                      </tr>
                      <tr className="bg-purple-600">
                        <td className="px-3 py-2 text-sm font-semibold text-white">Status</td>
                        <td className="px-3 py-2 text-sm text-white">
                          {paymentDetails.trns_sts || (currentLanguage === 'en' 
                            ? 'Transaction details not found. Please wait and try again.' 
                            : 'లావాదేవీ వివరాలు కనుగొనబడలేదు. దయచేసి వేచి ఉండి మళ్లీ ప్రయత్నించండి.')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Verify Payment Button */}
                <button
                  onClick={handleVerifyPayment}
                  disabled={isVerifyingPayment}
                  className="w-full bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifyingPayment ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      {currentLanguage === 'en' ? 'Verifying...' : 'ధృవీకరిస్తోంది...'}
                    </span>
                  ) : (
                    'VERIFY PAYMENT'
                  )}
                </button>

                {/* Receipt Download Link */}
                {paymentDetails.rcpt_url && (
                  <div className="text-center">
                    <a 
                      href={paymentDetails.rcpt_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 underline text-sm"
                    >
                      {currentLanguage === 'en' 
                        ? 'Please click here to download "Receipt".' 
                        : 'దయచేసి "రసీదు"ను డౌన్‌లోడ్ చేయడానికి ఇక్కడ క్లిక్ చేయండి.'}
                    </a>
                  </div>
                )}

                {/* Instruction Text */}
                <p className="text-xs text-gray-600 text-center mt-2">
                  {currentLanguage === 'en' 
                    ? "Click on the button 'Verify Payment' for successful or failing payments." 
                    : "విజయవంతమైన లేదా విఫలమైన చెల్లింపుల కోసం 'చెల్లింపును ధృవీకరించండి' బటన్‌పై క్లిక్ చేయండి."}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

